import * as _ from "lodash";
import * as xml2js from "xml2js";

import {
    DataUnavailableDictionaryDefinition
} from "../../stores/dictionary/DataUnavailableDictionaryDefinition";
import { DictionaryDefinition } from "../../stores/dictionary/DictionaryDefinition";
import { Word } from "../../stores/word/Word";
import { Logging } from "../../utils/Logging";
import { StringUtils } from "../../utils/StringUtils";
import { DictionaryAudioParser } from "./DictionaryAudioParser";

export const END_LINE = "\n";

export const UNKNOWN_ETYMOLOGY = "-";

export const UNKNOWN_VALUE = "(unknown)";

export const NULL_DEFINITION = "(not available)";

const MAX_DEFINITONS_COUNT = 4;

export namespace DictionaryResponseParserFromText {
  export function parseXmlFromText(
    word: Word,
    xmlText: string,
    logLevel: Logging.LogLevel = Logging.LogLevel.Warn
  ): Promise<DictionaryDefinition> {
    return new Promise<DictionaryDefinition>((resolve, reject) => {
      if (!xmlText || xmlText.trim().length === 0) {
        resolve(new DataUnavailableDictionaryDefinition(word.word));
        return;
      }

      xml2js.parseString(xmlText, (error, data) => {
        if (error) {
          reject(error);
          return;
        }

        if (!data.entry_list.entry) {
          resolve(new DataUnavailableDictionaryDefinition(word.word));
          return;
        }

        try {
          const { record, firstSound } = findBestRecord(
            data.entry_list,
            word,
            xmlText,
            logLevel
          );

          if (!record) {
            resolve(new DataUnavailableDictionaryDefinition(word.word));
            return;
          }

          let sound = record.sound || firstSound;

          // note: for images, there is a field record.art, but is almost never set (only 1 word 'ginseng' in current set?)

          const definition = findBestDefinition(record.def);
          const defn = new DictionaryDefinition(
            word.word,
            (definition && definition.date && definition.date[0]) ||
              UNKNOWN_VALUE,
            getTypeAsText(record.fl || UNKNOWN_VALUE),
            cleanDefinition(
              getAbbreviatedDefinitions(
                _parseDefinitions(definition, word.word, logLevel),
                logLevel
              )
            ),
            _parseEtymology(record.et, xmlText, word.word, logLevel),
            DictionaryAudioParser.parseAudioUrl(sound)
          );

          Logging.logDebug(logLevel, ["resolving word", word.word]);
          Logging.logDebugDir(logLevel, "as", defn);

          resolve(defn);
        } catch (error) {
          console.error("exception whilst parsing", error);
          reject(error);
        }
      });
    });
  }

  function cleanDefinition(definition: string): string {
    definition = StringUtils.replaceAll(definition, "  ", " ");
    definition = StringUtils.replaceAll(definition, "-:", "- ");

    definition = definition.split(":").join(END_LINE + "- ");

    definition = StringUtils.replaceAll(definition, "- " + END_LINE, "");

    return definition.trim();
  }

  function findBestDefinition(definition: any): any {
    if (_.isArray(definition)) {
      return definition[0];
    }
    return definition;
  }

  function findBestRecord(
    entry_list: any,
    word: Word,
    xmlText: string,
    logLevel: Logging.LogLevel
  ): {
    record: any;
    firstSound: any;
  } {
    let record = entry_list.entry;
    let firstSound = null;

    if (_.isArray(record)) {
      let records = record.filter(e => {
        return (
          e["$"].id
            .toLowerCase()
            .indexOf(word.word.replace("_", " ").toLowerCase()) >= 0 &&
          (word.wordType.length === 0 || e.fl[0] === word.wordType)
        );
      });

      firstSound = records.filter(r => !!r.sound).map(r => r.sound)[0];

      if (records.length === 0) {
        Logging.logWarn(logLevel, [
          "could not match word to dictionary, by id",
          word.word
        ]);
        record = null;
      } else if (records.length === 1) {
        record = records[0];
      } else if (records.length > 1) {
        record = tryToMatchByEtymology(records, xmlText, word, logLevel);
      }

      if (!record) {
        // get the 1st of many records.
        // TODO - could merge together, into definitions? but better to present as a list of definitions.
        record = _.first(entry_list.entry);
      }
    }

    return {
      record: record,
      firstSound: firstSound
    };
  }

  let _parseEtymology = function(
    ets,
    xmlText,
    wordName: string,
    logLevel: Logging.LogLevel
  ): string {
    Logging.logDebug(logLevel, ["_parseEtymology"]);

    if (!ets) {
      Logging.logWarn(logLevel, [
        "could not parse etymology for word",
        wordName
      ]);
      return UNKNOWN_ETYMOLOGY;
    }

    // The xml2js library loses the order of the elements.
    //
    // rebuild the etymology:
    // - find the first ety in the raw XML
    // - search forward until </et>
    // - collapse into text

    Logging.logDebugDir(logLevel, "ets", ets);

    if (ets.length > 1) {
      Logging.logWarn(logLevel, ["dropping some etys for word", wordName]);
    }

    let firstEty = ets[0];

    if (typeof firstEty === "string") {
      return firstEty;
    }

    const xmlTextTweaked = StringUtils.unescapeXmlChars(
      xmlText.replace("(now ", "(now  ")
    );

    let activeFirstEty = null;
    if (firstEty["_"]) {
      activeFirstEty = firstEty["_"];
    } else if (firstEty["it"]) {
      // special case for word 'baloney'
      activeFirstEty = firstEty["it"][0];
    }

    if (activeFirstEty) {
      const firstEtyCand1 = activeFirstEty.split("  ");
      // special case for word 'connoisseur' :(
      const firstEtyCand2 = activeFirstEty.split(" )");

      let activeFirstEty2 =
        firstEtyCand1[0].length < firstEtyCand2[0].length
          ? firstEtyCand1[0]
          : firstEtyCand2[0];

      let etyResult = tryGetEtymologyHtml(activeFirstEty2, xmlTextTweaked);
      if (!etyResult.etymologyHtml) {
        // try again - for word 'moped'
        etyResult = tryGetEtymologyHtml(
          activeFirstEty2.split(" ")[0],
          xmlTextTweaked
        );
      }
      if (!etyResult.etymologyHtml) {
        Logging.logWarn(logLevel, [
          "could not parse etymology from raw XML 2 for word",
          wordName,
          `from '${etyResult.xmlRemainderParts}'`
        ]);
        return UNKNOWN_ETYMOLOGY;
      }

      return cleanEtymologyHtml(etyResult.etymologyHtml);
    }

    Logging.logWarn(logLevel, [
      "could not parse etymology (finally) for word",
      wordName
    ]);
    return UNKNOWN_ETYMOLOGY;
  };

  const tryToMatchByEtymology = (
    records: any[],
    xmlText: string,
    word: Word,
    logLevel: Logging.LogLevel
  ): any => {
    records.forEach(e => {
      const etymology = _parseEtymology(e.et, xmlText, word.word, logLevel);

      if (!etymology) {
        return;
      }

      // use etymology anyway (we may have a mistake in google sheets)
      e.etymology = etymology;

      // 'Spanish (USA)' -> 'Spanish'
      const originalLanguageFirstWord = word.originalLanguage.split(" ")[0];

      if (
        etymology.indexOf(originalLanguageFirstWord) >= 0 ||
        etymology.indexOf(word.country) >= 0
      ) {
        e.matchedByEtymologyScore = 20;
      } else if (etymology.toLowerCase().indexOf(word.originalWord) >= 0) {
        e.matchedByEtymologyScore = 10;
      }

      if (e["$"].id === word.word) {
        e.matchedByEtymologyScore += 1;
      }
    });

    if (records.length > 0) {
      const matchedByEtymology = records.filter(
        r => !!r.matchedByEtymologyScore
      );
      matchedByEtymology.sort(
        (r1, r2) => r2.matchedByEtymologyScore - r1.matchedByEtymologyScore
      );
      if (matchedByEtymology.length > 0) {
        return matchedByEtymology[0];
      } else {
        const someEtymology = records.filter(r => !!r.etymology);
        if (someEtymology.length > 0) {
          return someEtymology[0];
        } else {
          return records[0];
        }
      }
    }

    return null;
  };

  const cleanEtymologyHtml = (etymologyHtml: string): string => {
    let etymologyText = StringUtils.replaceAll(etymologyHtml, "<it>", "'");
    etymologyText = StringUtils.replaceAll(etymologyText, "</it>", "'");

    etymologyText = StringUtils.replaceAll(etymologyText, ",'", "'");

    etymologyText = StringUtils.replaceAll(etymologyText, "  ", " ");

    etymologyText = StringUtils.replaceAll(etymologyText, "EGmc-WGmc ", "");

    // remove any trailing XML
    // e.g. for word 'baloney'
    etymologyText = etymologyText.split("<")[0];

    return StringUtils.capitalise(etymologyText).trim();
  };

  const tryGetEtymologyHtml = (
    firstEty: string,
    xmlTextTweaked: string
  ): { etymologyHtml: string; xmlRemainderParts: string } => {
    const xmlTextParts = StringUtils.splitFirst(xmlTextTweaked, firstEty);
    xmlTextParts.shift();
    const xmlRemainderParts = xmlTextParts.join(" ");

    const xmlTextParts2 = xmlRemainderParts.split("</et>");
    if (xmlTextParts.length === 0) {
      return {
        etymologyHtml: null,
        xmlRemainderParts: xmlRemainderParts
      };
    }

    const etymologyHtml = firstEty + xmlTextParts2[0];
    return {
      etymologyHtml: etymologyHtml,
      xmlRemainderParts: xmlRemainderParts
    };
  };

  let _parseDefinitions = function(
    def,
    wordName: string,
    logLevel: Logging.LogLevel
  ) {
    Logging.logDebug(logLevel, ["_parseDefinitions"]);

    let nullText = NULL_DEFINITION;
    if (!def) {
      return [nullText];
    }

    // TODO we are skipping some minor uses here - see data (def.sn - e.g. 'abdomen')

    Logging.logDebugDir(logLevel, "def", def);

    let definitions = def.dt;

    if (!definitions) {
      return [nullText];
    }

    if (!_.isArray(definitions)) {
      // special case for some words :-(
      if (definitions.sx) {
        definitions = definitions.sx;
      }
      definitions = [definitions];
    }

    let defSxs = definitions
      .map(def => {
        Logging.logDebugDir(logLevel, "defs.def", def);

        if (typeof def === "string") {
          return def;
        }

        const extractDefinitionUsingProp = (
          holder: any,
          prop: string
        ): string | null => {
          if (holder[prop]) {
            if (typeof holder[prop] === "string") {
              return holder[prop];
            }

            if (_.isArray(holder[prop])) {
              if (holder[prop].length > 1) {
                Logging.logWarn(logLevel, [
                  `dropping some definitions (${prop} array)`,
                  wordName
                ]);
              }

              const extracted = holder[prop][0];

              if (typeof extracted !== "string") {
                const subKeys = "it;_;";
                const extractedWithSubs = Object.keys(extracted)
                  .map(subKey => {
                    if (subKeys.indexOf(subKey + ";") >= 0) {
                      const extractedWithSub = extractDefinitionUsingProp(
                        extracted,
                        subKey
                      );

                      if (subKey === "it") {
                        return `'${extractedWithSub}'`;
                      }

                      return extractedWithSub;
                    }
                    return null;
                  })
                  .filter(extractedSub => !!extractedSub)
                  .join(" ");

                if (extractedWithSubs) {
                  return extractedWithSubs;
                }

                Logging.logWarn(logLevel, [
                  `dropping a definition (${extracted} - not a string or recognised subKey)`,
                  wordName
                ]);

                return null;
              }

              return extracted;
            }
          }
          return null;
        };

        const fixUpExtractedDefinition = (
          extractedDefinition: string
        ): string => {
          // xml2js loses order of text nodes vs other nodes.
          //
          // re-populate "  "/() with following props, if there are any:

          const props = "d_link;it;";

          let linkedWords = [];
          Object.keys(def).forEach(key => {
            if (props.indexOf(key + ";") >= 0 && def[key]) {
              let values = def[key];

              if (!_.isArray(values)) {
                values = [values];
              }

              linkedWords = linkedWords.concat(values);
            }
          });

          linkedWords = linkedWords.filter(lw => !!lw);

          const tokens = ["  ", "()"];
          const replacementFormats = [" $ ", " ($) "];

          linkedWords.forEach(linked => {
            extractedDefinition = StringUtils.replaceFirstToken(
              extractedDefinition,
              tokens,
              linked,
              replacementFormats
            );
          });

          return extractedDefinition.trim();
        };

        const supportedProps = "dt;sx;un;_;";

        const definitionsFromProps = Object.keys(def)
          .map(defProp => {
            if (supportedProps.indexOf(defProp + ";") >= 0) {
              const extracted = extractDefinitionUsingProp(def, defProp);

              return extracted;
            }
            return null;
          })
          .filter(defn => !!defn && defn !== ":");

        if (definitionsFromProps.length > 0) {
          return definitionsFromProps.map(fixUpExtractedDefinition).join(":");
        }

        Logging.logWarn(logLevel, [
          `cannot parse out definition from props ${supportedProps} for word`,
          wordName
        ]);
        return null;
      })
      .filter(def => !!def);

    let definitionTexts = defSxs
      .map(sx => {
        if (typeof sx === "string") {
          return sx;
        }

        if (sx["_"]) {
          return sx["_"];
        }

        Logging.logWarn(logLevel, [
          `dropping definition '${sx}' for ${wordName}`
        ]);
        return "";
      })
      .map(defn => defn.trim());

    // note: could filter a word to 1 definition, by number (sx.sxn) (new column in Google sheet)
    // but not needed so far, and seems bit too coupled to this dictionary data.

    const nonEmptyDefinitions = definitionTexts.filter(
      def => def && def.length > 0
    );

    Logging.logDebug(logLevel, ["_parseDefinition - end"]);

    return _.uniq(nonEmptyDefinitions);
  };

  function getAbbreviatedDefinitions(
    definitions: any[],
    logLevel: Logging.LogLevel
  ): string {
    Logging.logDebug(logLevel, ["getAbbreviatedDefinitions", definitions]);

    const defsAbbreviated = _.take(definitions, MAX_DEFINITONS_COUNT).map(
      function(def) {
        const maxLen = 120;
        if (def.length > maxLen) {
          def = def.substr(0, maxLen) + "...";
        }
        return def;
      }
    );

    if (definitions.length === 0) {
      return UNKNOWN_VALUE;
    }

    if (defsAbbreviated.length === 1) {
      let definition = defsAbbreviated[0];

      if (definition.startsWith(":")) {
        definition = definition.substr(1);
      }

      return StringUtils.capitalise(definition);
    }

    defsAbbreviated.unshift("");
    return defsAbbreviated.reduce((memo, currentDef) => {
      const bullet = "- ";

      return memo + bullet + currentDef + END_LINE;
    });
  }

  function getTypeAsText(wordType: string): string {
    if (wordType === UNKNOWN_VALUE) {
      return wordType;
    }

    return "[" + wordType + "]";
  }
}



// WEBPACK FOOTER //
// ./services/dictionary/DictionaryResponseParserFromText.ts